import { useGetPowerBiReportQuery } from 'common/store/api/powerBi.api';
import FullLayout from '../../Layouts/FullLayout';
import usePowerBiReport from 'common/hooks/usePowerBiReport';
import {
  PowerBiReportId,
  PowerBiReportParams,
  PowerBiWorkspaceId,
} from 'common/models/power-bi.model';
import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';
import { PowerBIReportComponent } from 'common/components/powerbi/PowerBIReportComponent';
import ErrorPage, { ErrorType } from 'common/components/errorPages/ErrorPage';
import { Spinner } from 'common/components/loader/Spinner';
import useNavigationMenus from 'common/hooks/useMenus';
import { useNavigateWithProjectId } from 'common/hooks/route';
import { useEffect } from 'react';

function PowerBiModule() {
  const { reportDetails } = usePowerBiReport();
  const { activeProject } = useModuleProjectContext();
  const { currentPageDetails } = useNavigationMenus();
  const navigate = useNavigateWithProjectId();

  // Get the workspace ID from the active project
  const workspaceId = activeProject?.powerBIWorkspaceID as PowerBiWorkspaceId;

  // Get the Power BI report data
  const { isLoading, data, error } = useGetPowerBiReportQuery(
    {
      workspaceId: workspaceId,
      powerBiReportId: reportDetails?.reportId as PowerBiReportId,
    },
    {
      skip: !workspaceId || !reportDetails?.reportId || !!currentPageDetails?.children?.length,
    }
  );

  // If the current page has children, navigate to the first child
  useEffect(() => {
    if (currentPageDetails?.children?.length) {
      navigate(currentPageDetails?.children?.[0]?.route as string);
    }
  }, [currentPageDetails, navigate]);

  // If the workspace ID or report ID is not set, show an error page
  if (!workspaceId || !reportDetails?.reportId) {
    return (
      <FullLayout>
        <ErrorPage
          errorType={ErrorType.ERROR}
          titleOverride="Configuration Error"
          messageOverride={`${!workspaceId ? 'Workspace ID is not set' : 'Report ID is not set'}. Please try again or contact support if the issue persists.`}
        />
      </FullLayout>
    );
  }

  // If the report is loading, show a spinner
  if (isLoading) {
    return (
      <FullLayout>
        <Spinner />
      </FullLayout>
    );
  }

  // If the report is an error, show an error page
  if (error) {
    return (
      <FullLayout>
        <ErrorPage errorType={ErrorType.ERROR} />
      </FullLayout>
    );
  }

  // If the report is loaded, show the report component
  return (
    <FullLayout>
      <PowerBIReportComponent
        embedParams={{ ...data, pageName: reportDetails?.pageName ?? '' } as PowerBiReportParams}
        isPaginatedReport={data?.embedUrl?.includes('/rdlEmbed')}
      />
    </FullLayout>
  );
}

export default PowerBiModule;
